import { Children, type PropsWithChildren } from 'react';
import {
  animated,
  easings,
  useSprings,
  type SpringValues,
} from '@react-spring/web';
import { Box } from '@mentimeter/ragnar-ui/box';

const ANIMATION_DELAY = 80;
const ANIMATION_DURATION = 500;
// Cap delay to 1200ms to avoid long delays when there are many items
const MAX_DELAY = 1200;

export const MasonryGrid = ({
  numberOfColumns,
  shouldAnimate,
  initialDelay = 0,
  children,
}: PropsWithChildren<{
  numberOfColumns: number;
  shouldAnimate: boolean;
  initialDelay?: number;
}>) => {
  const childrenArray = Children.toArray(children);

  const [springs] = useSprings<{ opacity: number; x: number }>(
    childrenArray.length,
    (index) => ({
      from: { opacity: 0, x: -60 },
      opacity: 1,
      x: 0,
      delay: shouldAnimate
        ? Math.min(MAX_DELAY, ANIMATION_DELAY * index + initialDelay)
        : 0,
      config: {
        duration: ANIMATION_DURATION,
        easing: easings.easeOutBack,
      },
      immediate: !shouldAnimate,
    }),
  );

  return (
    <Box height="auto" width="100%" flexDirection="row">
      {Array.from(Array(numberOfColumns).keys()).map((columnIndex) => (
        <Box
          key={columnIndex}
          flexDirection="column"
          flex={1}
          p={1}
          mr={columnIndex === numberOfColumns - 1 ? 0 : '1.8%'}
          gap={4}
          height="100%"
          maxHeight="100%"
        >
          {childrenArray.map((child, index) => {
            if (index % numberOfColumns !== columnIndex) {
              return null;
            }
            return (
              <Box
                key={`${index}-${columnIndex}`}
                as={animated.div}
                style={springs[index] as SpringValues}
                width="100%"
              >
                {child}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};
